// Chat with Breeze about a prep. Turns are kept per prep in sessionStorage, so
// switching preps and coming back finds the thread. A reply that edited the open
// prep tells the editor to re-read it now; a reply that made a new prep shows a
// chip that opens it.

import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { CHAT_LIMITS, type PrepChatTurn } from "@breeze/core";
import { chatPrep } from "../lib/prepApi";
import { touchesSlug } from "../lib/prepEdit";
import { TextBox } from "./TextBox";

interface PrepChatProps {
  readonly slug: string | null;
  /** The call's live text (notes, steps, what Breeze said), read when a message is sent. */
  readonly live?: () => string;
  /** The reply edited the open prep. */
  readonly onChanged?: () => void;
  /** Open a prep the reply made. */
  readonly onOpen?: (slug: string) => void;
  /** Dark surface (a call), not the light page. */
}

interface Msg extends PrepChatTurn {
  readonly open?: { readonly slug: string; readonly title: string };
}

const MAX_TURNS = 24;
/** How often the thread is marked as in use while it is open. */
const SEEN_EVERY_MS = 30_000;

function storeKey(slug: string | null): string {
  return `breeze:prep-chat:${slug ?? "none"}`;
}

function seenKey(slug: string | null): string {
  return `${storeKey(slug)}:seen`;
}

/** Marks the thread as in use right now. A thread nobody has had open for
 * CHAT_LIMITS.quietClearsMs is dropped the next time it is read (Adam,
 * 2026-10-13: "stuff i leave in the chat should not persist if i leave a
 * meeting for 5 minutes or more"). */
function markSeen(slug: string | null): void {
  try {
    sessionStorage.setItem(seenKey(slug), String(Date.now()));
  } catch {
    // Storage is blocked: the thread lives on screen only.
  }
}

function readMsgs(slug: string | null): Msg[] {
  try {
    const seen = Number(sessionStorage.getItem(seenKey(slug)));
    if (seen > 0 && Date.now() - seen >= CHAT_LIMITS.quietClearsMs) {
      sessionStorage.removeItem(storeKey(slug));
      sessionStorage.removeItem(seenKey(slug));
      return [];
    }
    const raw = sessionStorage.getItem(storeKey(slug));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((m): m is Msg => !!m && typeof m === "object" && typeof (m as Msg).text === "string" && ((m as Msg).role === "user" || (m as Msg).role === "breeze"));
  } catch {
    return [];
  }
}

function writeMsgs(slug: string | null, msgs: readonly Msg[]): void {
  try {
    sessionStorage.setItem(storeKey(slug), JSON.stringify(msgs.slice(-MAX_TURNS)));
    markSeen(slug);
  } catch {
    // Storage is blocked or full: the thread lives on screen only.
  }
}

export function PrepChat({ slug, live, onChanged, onOpen }: PrepChatProps): JSX.Element {
  const [msgs, setMsgs] = useState<Msg[]>(() => readMsgs(slug));
  const [draft, setDraft] = useState("");
  const [typing, setTyping] = useState(false);
  const [error, setError] = useState("");
  const slugRef = useRef(slug);
  const listRef = useRef<HTMLDivElement>(null);
  const callbacks = useRef({ onChanged, onOpen });
  callbacks.current = { onChanged, onOpen };

  useEffect(() => {
    slugRef.current = slug;
    setMsgs(readMsgs(slug));
    setTyping(false);
    setError("");
    // In use from now until it is closed, switched away from, or the tab is
    // put away; the last mark is the moment he left.
    markSeen(slug);
    const beat = window.setInterval(() => markSeen(slug), SEEN_EVERY_MS);
    const onHide = (): void => markSeen(slug);
    window.addEventListener("pagehide", onHide);
    return () => {
      window.clearInterval(beat);
      window.removeEventListener("pagehide", onHide);
      markSeen(slug);
    };
  }, [slug]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, typing]);

  const send = useCallback(
    async (text: string, base: Msg[]): Promise<void> => {
      const asked = slugRef.current;
      const next: Msg[] = [...base, { role: "user", text }];
      setMsgs(next);
      writeMsgs(asked, next);
      setError("");
      setTyping(true);
      try {
        const turns: PrepChatTurn[] = next.slice(-MAX_TURNS).map((m) => ({ role: m.role, text: m.text }));
        let liveText = "";
        try {
          liveText = live?.() ?? "";
        } catch {
          liveText = "";
        }
        const reply = await chatPrep({ ...(asked ? { slug: asked } : {}), messages: turns, ...(liveText ? { live: liveText } : {}) });
        const answer: Msg = {
          role: "breeze",
          text: reply.reply,
          ...(reply.created ? { open: { slug: reply.created.slug, title: reply.created.title } } : {}),
        };
        const all = [...next, answer];
        writeMsgs(asked, all);
        if (slugRef.current === asked) {
          setMsgs(all);
          setTyping(false);
        }
        if (asked && touchesSlug(reply.changed, asked)) callbacks.current.onChanged?.();
      } catch (err: unknown) {
        if (slugRef.current === asked) {
          setTyping(false);
          setError(err instanceof Error ? err.message : "Breeze did not answer. Try again.");
        }
      }
    },
    [live],
  );

  const submit = useCallback((): void => {
    const text = draft.trim();
    if (!text || typing) return;
    setDraft("");
    void send(text, msgs);
  }, [draft, msgs, send, typing]);

  const retry = useCallback((): void => {
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== "user" || typing) return;
    void send(last.text, msgs.slice(0, -1));
  }, [msgs, send, typing]);


  return (
    <div className="pc">
      <div className="pc__list" ref={listRef} role="log" aria-live="polite">
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "pc__msg pc__msg--user" : "pc__msg"}>
            <p className="pc__text">{m.text}</p>
            {m.open ? (
              <button type="button" className="pc__open" onClick={() => callbacks.current.onOpen?.(m.open?.slug ?? "")}>
                Open {m.open.title}
              </button>
            ) : null}
          </div>
        ))}
        {typing ? (
          <div className="pc__msg pc__typing" aria-label="Breeze is typing">
            <span />
            <span />
            <span />
          </div>
        ) : null}
        {error ? (
          <div className="pc__error" role="alert">
            <span>{error}</span>
            <button type="button" className="pc__retry" onClick={retry}>
              Try again
            </button>
          </div>
        ) : null}
      </div>
      <form
        className="pc__form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <TextBox
          className="pc__input"
          value={draft}
          placeholder="Ask Breeze"
          ariaLabel="Message to Breeze"
          onValue={setDraft}
          onEnter={submit}
        />
        <button type="submit" className="pc__send" disabled={typing || draft.trim() === ""}>
          Send
        </button>
      </form>
    </div>
  );
}
